// Shared meeting-options panel — JSX ported verbatim from App.js so /v2
// (and eventually App.js) renders the same UI from a single source.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, CalendarPlus } from 'lucide-react';

export default function MeetingOptionsPanel({ meeting }) {
  const { t } = useTranslation();
  const {
    meetingOptions,
    meetingTitle, setMeetingTitle,
    meetingDescription, setMeetingDescription,
    showTimeControls,
    removeMeetingOption,
    updateMeetingTime,
    toggleTimeControls,
    closeMeetingOptions,
    copyAllOptions,
    sendOptionsToGmail,
    createSingleGCalEvent,
    createICalFiles,
    use24HourFormat,
  } = meeting;

  if (meetingOptions.length === 0) return null;

  return (
    <section className="meeting-options-section" aria-label={t('meeting.options')}>
      <div className="meeting-options-header">
        <h2>{t('meeting.options')}</h2>
        <button
          className="close-meeting-options-btn"
          onClick={closeMeetingOptions}
          aria-label={t('buttons.closeMeetingDetails')}
        >×</button>
      </div>

      <div className="meeting-info-fields">
        <div className="meeting-field">
          <label htmlFor="meeting-title">{t('meeting.title')}</label>
          <input
            id="meeting-title"
            type="text"
            value={meetingTitle}
            onChange={(e) => setMeetingTitle(e.target.value)}
            placeholder={t('meeting.titlePlaceholder')}
            className="meeting-title-input"
          />
        </div>
        <div className="meeting-field">
          <label htmlFor="meeting-description">{t('meeting.description')}</label>
          <textarea
            id="meeting-description"
            value={meetingDescription}
            onChange={(e) => setMeetingDescription(e.target.value)}
            placeholder={t('meeting.descriptionPlaceholder')}
            className="meeting-description-textarea"
            rows="1"
          />
        </div>
      </div>

      <div className="meeting-options-list">
        {meetingOptions.map((option, index) => {
          const showControls = showTimeControls[option.id] || false;
          return (
            <div key={option.id} className="meeting-option-item">
              <div className="meeting-option-header">
                <h4>{t('meeting.option', { number: index + 1 })}</h4>
                <div className="meeting-option-actions">
                  <button
                    className="option-icon-btn option-time-btn"
                    onClick={() => toggleTimeControls(option.id)}
                    aria-label={t('meeting.editTime')}
                  >
                    <Clock size={14} />
                    <span className="option-icon-tooltip">{t('meeting.editTime')}</span>
                  </button>
                  <button
                    className="option-icon-btn option-gcal-btn"
                    onClick={() => createSingleGCalEvent(option, index)}
                    aria-label={t('meeting.addToGoogleCalendar')}
                  >
                    <CalendarPlus size={14} />
                    <span className="option-icon-tooltip">{t('meeting.addToCalendar')}</span>
                  </button>
                  <button
                    className="remove-option-btn"
                    onClick={() => removeMeetingOption(option.id)}
                    aria-label={t('meeting.removeOption', { number: index + 1 })}
                  >×</button>
                </div>
              </div>

              {showControls && (
                <div className="meeting-time-controls">
                  <div className="time-control-group">
                    <label htmlFor={`start-time-${option.id}`}>{t('meeting.startTime')}</label>
                    <div className="time-inputs">
                      <select
                        id={`start-hour-${option.id}`}
                        value={option.startDateTime.hour}
                        onChange={(e) => updateMeetingTime(option.id, e.target.value, option.startDateTime.minute, option.duration)}
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>
                            {use24HourFormat
                              ? i.toString().padStart(2, '0')
                              : (i % 12 || 12) + (i < 12 ? t('time.am') : t('time.pm'))}
                          </option>
                        ))}
                      </select>
                      <span className="time-separator">:</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-5]?[0-9]"
                        id={`start-minute-${option.id}`}
                        className="minute-input"
                        value={option.startDateTime.minute.toString().padStart(2, '0')}
                        onClick={(e) => e.target.select()}
                        onChange={(e) => {
                          if (e.target.value === '') return;
                          const numValue = parseInt(e.target.value, 10);
                          if (!isNaN(numValue)) {
                            const value = Math.min(59, Math.max(0, numValue));
                            updateMeetingTime(option.id, option.startDateTime.hour, value, option.duration);
                          }
                        }}
                        onBlur={(e) => {
                          let value = parseInt(e.target.value, 10);
                          if (isNaN(value)) value = 0;
                          value = Math.min(59, Math.max(0, value));
                          updateMeetingTime(option.id, option.startDateTime.hour, value, option.duration);
                        }}
                      />
                    </div>
                  </div>
                  <div className="time-control-group">
                    <label htmlFor={`duration-${option.id}`}>{t('meeting.duration')}</label>
                    <select
                      id={`duration-${option.id}`}
                      value={option.duration}
                      onChange={(e) => updateMeetingTime(option.id, option.startDateTime.hour, option.startDateTime.minute, e.target.value)}
                    >
                      <option value="15">{t('meeting.durations.15')}</option>
                      <option value="30">{t('meeting.durations.30')}</option>
                      <option value="45">{t('meeting.durations.45')}</option>
                      <option value="60">{t('meeting.durations.60')}</option>
                      <option value="90">{t('meeting.durations.90')}</option>
                      <option value="120">{t('meeting.durations.120')}</option>
                      <option value="180">{t('meeting.durations.180')}</option>
                    </select>
                  </div>
                </div>
              )}

              <pre className="meeting-option-text">{option.text}</pre>
            </div>
          );
        })}
      </div>

      <div className="meeting-options-actions">
        <button className="option-action-btn" onClick={copyAllOptions}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          {t('meeting.copyAll')}
        </button>
        <button className="option-action-btn" onClick={sendOptionsToGmail}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          {t('meeting.sendToGmail')}
        </button>
        <button className="option-action-btn" onClick={createICalFiles}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
            <path d="M8 14h.01" />
            <path d="M12 14h.01" />
            <path d="M16 14h.01" />
            <path d="M8 18h.01" />
            <path d="M12 18h.01" />
            <path d="M16 18h.01" />
          </svg>
          {t('meeting.downloadICal')}
        </button>
      </div>
    </section>
  );
}
